'use client'

import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'

export function AnimatedCards() {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: false, amount: 0.4 })

  return (
    <div
      ref={ref}
      className="relative max-w-4xl mx-auto mb-8 md:mb-12 h-[240px] sm:h-[300px] md:h-[400px]"
    >
      {/* Left card */}
      <motion.div
        initial={{ x: -120 }}
        animate={isInView ? { x: 0 } : { x: -120 }}
        transition={{ duration: 2.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.3 }}
        className="absolute -left-2 sm:-left-8 top-[52%] -translate-y-1/2 w-[180px] sm:w-[280px] md:w-[340px] h-[190px] sm:h-[240px] md:h-[320px] bg-white rounded-2xl shadow-2xl overflow-hidden transform origin-center z-10 flex flex-col justify-center sm:block"
      >
        <div className="p-2.5 sm:p-6 md:p-8">
          <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-2">QUESTION 3 OF 7</p>
          <h3 className="text-xs sm:text-lg md:text-xl font-medium text-gray-900 mb-1.5 sm:mb-4 leading-tight">
            What is your country of citizenship?
          </h3>
          <div className="space-y-1 sm:space-y-2">
            <div className="flex items-center gap-1.5 sm:gap-2 border border-gray-200 rounded-lg px-2 sm:px-4 py-1 sm:py-2 text-[8px] sm:text-sm">
              <span className="text-sm sm:text-lg">🇺🇸</span>
              <span className="text-gray-700">United States</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 bg-gray-900 text-white rounded-lg px-2 sm:px-4 py-1 sm:py-2 text-[8px] sm:text-sm">
              <span className="text-sm sm:text-lg">🇨🇦</span>
              <span>Canada</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 border border-gray-200 rounded-lg px-2 sm:px-4 py-1 sm:py-2 text-[8px] sm:text-sm">
              <span className="text-sm sm:text-lg">🇬🇧</span>
              <span className="text-gray-700">United Kingdom</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2 text-sm">
              <span className="text-lg">🇮🇳</span>
              <span className="text-gray-700">India</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2 text-sm">
              <span className="text-lg">🇨🇳</span>
              <span className="text-gray-700">China</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Center card - main focus */}
      <motion.div
        initial={{ scale: 1 }}
        animate={isInView ? { scale: 1.1, opacity: 1 } : { scale: 1 }}
        transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] sm:w-[300px] md:w-[380px] h-[210px] sm:h-[260px] md:h-[350px] bg-white rounded-2xl shadow-2xl overflow-hidden z-20 flex flex-col justify-center sm:block"
      >
        <div className="p-2.5 sm:p-6 md:p-8">
          <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-2">QUESTION 2 OF 7</p>
          <h3 className="text-xs sm:text-lg md:text-xl font-medium text-gray-900 mb-1.5 sm:mb-4 leading-tight">
            What best describes your immigration goal?
          </h3>
          <div className="grid grid-cols-2 gap-1 sm:gap-2 mb-4">
            <div className="border border-gray-200 rounded-lg p-1.5 sm:p-3 text-center">
              <div className="text-sm sm:text-2xl mb-0.5 sm:mb-1">💼</div>
              <p className="text-[8px] sm:text-xs text-gray-700 leading-tight">
                Work for a U.S. Employer
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-1.5 sm:p-3 text-center">
              <div className="text-sm sm:text-2xl mb-0.5 sm:mb-1">🚀</div>
              <p className="text-[8px] sm:text-xs text-gray-700 leading-tight">
                Transfer to U.S. office
              </p>
            </div>
            <div className="bg-gray-900 text-white rounded-lg p-1.5 sm:p-3 text-center">
              <div className="text-sm sm:text-2xl mb-0.5 sm:mb-1">🏢</div>
              <p className="text-[8px] sm:text-xs leading-tight">Start or invest in a business</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-1.5 sm:p-3 text-center">
              <div className="text-sm sm:text-2xl mb-0.5 sm:mb-1">📚</div>
              <p className="text-[8px] sm:text-xs text-gray-700 leading-tight">Study in the U.S.</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Right card */}
      <motion.div
        initial={{ x: 120 }}
        animate={isInView ? { x: 0, opacity: 1 } : { x: 120 }}
        transition={{ duration: 2.4, ease: [0.25, 0.1, 0.25, 1], delay: 0.3 }}
        className="absolute -right-2 sm:-right-8 top-[52%] -translate-y-1/2 w-[180px] sm:w-[280px] md:w-[340px] h-[190px] sm:h-[240px] md:h-[320px] bg-white rounded-2xl shadow-2xl overflow-hidden transform origin-center z-10 flex flex-col justify-center sm:block"
      >
        <div className="p-2.5 sm:p-6 md:p-8">
          <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-2">QUESTION 1 OF 7</p>
          <h3 className="text-xs sm:text-lg md:text-xl font-medium text-gray-900 mb-1.5 sm:mb-4 leading-tight">
            Who are you completing this assessment for?
          </h3>
          <div className="space-y-1 sm:space-y-2">
            <div className="bg-gray-900 text-white rounded-lg px-2 sm:px-4 py-1 sm:py-3 text-[8px] sm:text-sm text-left">
              Myself
            </div>
            <div className="border border-gray-200 rounded-lg px-2 sm:px-4 py-1 sm:py-3 text-[8px] sm:text-sm text-left text-gray-700">
              My spouse / partner or family member
            </div>
            <div className="border border-gray-200 rounded-lg px-2 sm:px-4 py-1 sm:py-3 text-[8px] sm:text-sm text-left text-gray-700">
              An employee or job candidate
            </div>
            <div className="hidden sm:block border border-gray-200 rounded-lg px-4 py-3 text-sm text-left text-gray-700">
              My company / future business in the U.S.
            </div>
            <div className="hidden sm:block border border-gray-200 rounded-lg px-4 py-3 text-sm text-left text-gray-700">
              Just exploring options
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
